import { Injectable } from '@angular/core';
import { SearchArea } from '@shared/enums/SearchArea';
import { TCenter } from '@shared/interfaces/TCenter';
import { TAccountRegion, TBrickSale, TProject, TRegionBrick } from '@shared/interfaces/TProject';
import { TSearchParams } from '@shared/interfaces/TSearchParams';
import { TUser } from '@shared/interfaces/TUser';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { BrickSalesSummary } from 'src/app/interfaces/brick-sales-summary.interface';
import { Region } from 'src/app/interfaces/region.interface';
import { TopAndBottomX } from 'src/app/interfaces/top-and-bottom-x.interface';
import { YearMonthDate } from 'src/app/interfaces/year-month-date.interface';
import { YearMonth } from 'src/app/interfaces/year-month.interface';
import { CategoriesService } from 'src/app/services/categories.service';
import { CentersService } from 'src/app/services/centers.service';

@Injectable({
  providedIn: 'root'
})
export class KamReportsService {

  private _allCenters$: BehaviorSubject<TCenter[]> = new BehaviorSubject<TCenter[]>([]);
  private _selectedCountries$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private _selectedRegions$: BehaviorSubject<Region[]> = new BehaviorSubject<Region[]>([]);

  constructor(
    private _centersService: CentersService,
    private _categoriesService: CategoriesService,
  ) { }

  get allCenters(): TCenter[] { return this._allCenters$.value }
  set allCenters(centers: TCenter[]) { this._allCenters$.next(centers) }
  get allCenters$(): Observable<TCenter[]> { return this._allCenters$ }

  get selectedCountries(): string[] { return this._selectedCountries$.value }
  set selectedCountries(countries: string[]) { this._selectedCountries$.next(countries) }
  get selectedCountries$(): Observable<string[]> { return this._selectedCountries$ }

  get selectedRegions(): Region[] { return this._selectedRegions$.value }
  set selectedRegions(regions: Region[]) { this._selectedRegions$.next(regions) }
  get selectedRegions$(): Observable<Region[]> { return this._selectedRegions$ }

  getProject(id: string): TProject | undefined {
    return this._categoriesService.getProject(id);
  }

  getAllProjectCenters$(projectId: string, countries: string[] = [], regions: string[] = []): Observable<TCenter[]> {
    // console.log('## getAllProjectCenters$ projectId', projectId);
    // console.log('## getAllProjectCenters$ countries', countries);
    // console.log('## getAllProjectCenters$ regions', regions);
    const params: TSearchParams<TCenter> = {
      area: SearchArea.Centers,
      paging: { pageSize: 0, pageIndex: 0, total: 0, sortBy: [], query: '' },
      projectId,
      query: '',
      results: [],
      countries: countries,
      showAllCountries: countries.includes('World'),
      projectRegions: { filter: !!regions.length, filterBy: regions }
    };

    return this._centersService.searchCentersRequest$(params).pipe(
      map(response => {
        return response.results;
      })
    );
  }

  // parse a value as number
  parseNumber(value: number | string): number {
    return typeof value === 'string' ? parseInt(value, 10) : value;
  }

  /**
   * Get all unique sales dates from bricks
   * @param  {TRegionBrick[]} bricks
   * @returns  { year: number; month: number; date: Date }[]
   */
  getAllSalesDates(bricks: TRegionBrick[]): YearMonthDate[] {
    const dateSet = new Set<string>();
    if (!bricks || bricks.length === 0) return [];
    bricks.forEach((brick) => {
      brick.sales.forEach((sale) => {
        const year = this.parseNumber(sale.year);
        const month = this.parseNumber(sale.month);
        dateSet.add(`${year}-${month.toString().padStart(2, '0')}`);
      });
    });
    return Array.from(dateSet).map(dateStr => {
      const [year, month] = dateStr.split('-').map(Number);
      return { year, month, date: new Date(year, month - 1) };
    });
  }

  /**
   * Get all unique sales dates for given competitor
   * @param  {TRegionBrick[]} bricks
   * @returns  { year: number; month: number; date: Date }[]
   */
  getAllCompetitorSalesDates(competitorId: string, bricks: TRegionBrick[]): YearMonthDate[] {
    const dateSet = new Set<string>();
    if (!bricks || bricks.length === 0) return [];
    bricks.forEach((brick) => {
      brick.salesCompetitors?.find(cmp => cmp.competitorId === competitorId)?.sales.forEach((sale) => {
        const year = this.parseNumber(sale.year);
        const month = this.parseNumber(sale.month);
        dateSet.add(`${year}-${month.toString().padStart(2, '0')}`);
      });
    });
    return Array.from(dateSet).map(dateStr => {
      const [year, month] = dateStr.split('-').map(Number);
      return { year, month, date: new Date(year, month - 1) };
    });
  }

  /**
   * Get total (sum of all data) of target field, for a specific month
   * @param  {TRegionBrick[]} bricks
   * @param  {number} year
   * @param  {number} month
   * @param  {keyofTBrickSale} target
   * @returns number
   */
  getTotalForMonth(bricks: TRegionBrick[], year: number, month: number, target: keyof TBrickSale): number {
    // console.log('## getTotalForMonth bricks', bricks);
    // console.log('## getTotalForMonth year', year);
    // console.log('## getTotalForMonth month', month);
    // console.log('## getTotalForMonth target', target);
    if (!bricks || bricks.length === 0) return 0;
    let total = 0;
    bricks.forEach((brick) => {
      brick.sales.forEach((sale) => {
        const saleYear = this.parseNumber(sale.year);
        const saleMonth = this.parseNumber(sale.month);
        if (saleYear === year && saleMonth === month) {
          total += this.parseNumber(sale[target]);
          // console.log('## getTotalForMonth month', brick.name, year, month, sale[target]);
        }
      });
    });
    // console.log('## getTotalForMonth total', total);
    return total;
  }


  /**
   * Get total (sum of all data) of target field, of all competitors, for a specific month
   * @param  {TRegionBrick[]} bricks
   * @param  {number} year
   * @param  {number} month
   * @param  {keyofTBrickSale} target
   * @returns number
   */
  getCompetitorsTotalForMonth(id: string, bricks: TRegionBrick[], year: number, month: number, target: keyof TBrickSale): number {
    // console.log('## getCompetitorsTotalForMonth id', id);
    // console.log('## getCompetitorsTotalForMonth bricks', bricks);
    // console.log('## getCompetitorsTotalForMonth year', year);
    // console.log('## getCompetitorsTotalForMonth month', month);
    // console.log('## getCompetitorsTotalForMonth target', target);
    if (!bricks || bricks.length === 0) return 0;
    let total = 0;
    bricks.forEach((brick) => {
      if (brick.salesCompetitors !== undefined && brick.salesCompetitors.length) {
        brick.salesCompetitors.filter(competitor => competitor.competitorId == id).forEach((salesCompetitors) => {
          salesCompetitors.sales?.forEach((sale) => {
            const saleYear = this.parseNumber(sale.year);
            const saleMonth = this.parseNumber(sale.month);
            if (saleYear === year && saleMonth === month) {
              total += this.parseNumber(sale[target]);
            }
          });
        });
      }
    });
    return total;
  }

  /**
   * Get closest sales date
   * @param  {TRegionBrick[]} bricks
   * @returns { year: number; month: number }
   */
  getClosestSalesDate(bricks: TRegionBrick[]): YearMonth {
    if (!bricks || bricks.length === 0) return { year: 0, month: 0 };
    const today = new Date();
    const allDates = this.getAllSalesDates(bricks);
    if (allDates.length === 0) return { year: 0 , month: 0 };
    const closestDate = allDates.reduce((prev, curr) => {
      const prevDiff = Math.abs(prev.date.getTime() - today.getTime());
      const currDiff = Math.abs(curr.date.getTime() - today.getTime());
      return currDiff < prevDiff ? curr : prev;
    });
    const result = { year: closestDate.year, month: closestDate.month };
    return result;
  }

  /**
   * Sum patients or sales from all bricks from closest month
   * @param  {TRegionBrick[]} bricks
   * @param  {keyofTBrickSale} target - name of the field to sum (patients or sales)
   * @returns number
   */
  getTotalFromClosestMonth(bricks: TRegionBrick[], target: keyof TBrickSale): number {
    // console.log('## getTotalFromClosestMonth bricks', bricks);
    // console.log('## getTotalFromClosestMonth target', target);
    if (!bricks || bricks.length === 0) return 0;
    const closestDate = this.getClosestSalesDate(bricks);
    if (!closestDate) return 0;
    const result = this.getTotalForMonth(bricks, closestDate.year, closestDate.month, target);
    // console.log('## getTotalFromClosestMonth result', result);
    return Math.round(result * 100) / 100;
  }

  /**
   * Sum patients or sales from all bricks from closest month
   * @param  {TRegionBrick[]} bricks
   * @param  {keyofTBrickSale} target - name of the field to sum (patients or sales)
   * @returns number
   */
  getCompetitorsTotalFromClosestMonth(id: string, bricks: TRegionBrick[], target: keyof TBrickSale): number {
    // console.log('## getTotalFromClosestMonth id', id);
    // console.log('## getTotalFromClosestMonth bricks', bricks);
    // console.log('## getTotalFromClosestMonth target', target);
    if (!bricks || bricks.length === 0) return 0;
    const closestDate = this.getClosestSalesDate(bricks);
    // console.log('## getTotalFromClosestMonth closestDate', closestDate);
    if (!closestDate) return 0;
    const result = this.getCompetitorsTotalForMonth(id, bricks, closestDate.year, closestDate.month, target);
    // console.log('## getCompetitorsTotalFromClosestMonth result', result);
    return Math.round(result * 100) / 100;
  }

  /**
   * Get momentum data for patients or sales from all bricks from the last X months
   * @param  {TRegionBrick[]} bricks
   * @param  {noMonths} string - number of months
   * @param  {keyofTBrickSale} target - field to count
   * @returns number
   */
  getLastXMonthsTotal(bricks: TRegionBrick[], noMonths: number, target: keyof TBrickSale): number[] {
    if (!bricks || bricks.length === 0) return [];
    const allDates = this.getAllSalesDates(bricks);
    if (allDates.length === 0) return [];
    // Sort dates in descending order (newest first)
    const sortedDates = [...allDates].sort((a, b) => b.date.getTime() - a.date.getTime());
    // Get the noMonths most recent months (or fewer if we don't have enough data)
    const lastXMonths = sortedDates.slice(0, noMonths);
    // Calculate totals for each month and return in chronological order (oldest first)
    const result = lastXMonths.map(({ year, month }) => this.getTotalForMonth(bricks, year, month, target)).reverse();
    // console.log('## getLastXMonthsTotal result', result);
    return result;
  }

  getLastXMonthsCompetitorsTotal(competitorId: string, bricks: TRegionBrick[], noMonths: number, field: keyof TBrickSale): number[] {
    if (!bricks || bricks.length === 0) return [];
    const allDates = this.getAllCompetitorSalesDates(competitorId, bricks);
    if (allDates.length === 0) return [];
    // Sort dates in descending order (newest first)
    const sortedDates = [...allDates].sort((a, b) => b.date.getTime() - a.date.getTime());
    // Get the noMonths most recent months (or fewer if we don't have enough data)
    const lastXMonths = sortedDates.slice(0, noMonths);
    // Calculate totals for each month and return in chronological order (oldest first)
    const result = lastXMonths.map(({ year, month }) => this.getCompetitorsTotalForMonth(competitorId, bricks, year, month, field)).reverse();
    // console.log('## getLastXMonthsTotal result', result);
    return result;
  }

  /**
   * Build an array of objects with bricks name, sales summary,
   * sales momentum short and long, from the last x months
   * @param  {TRegionBrick[]} bricks
   * @param  {number} noMonths - number of months
   * @returns BrickSalesSummary
   */
  getBricksWithLastXMonthsSales(bricks: TRegionBrick[], noMonths: number): BrickSalesSummary[] {
    if (!bricks || bricks.length === 0) return [];
    // First get all unique dates from the sales data
    const allDates = this.getAllSalesDates(bricks);
    if (allDates.length === 0) return [];
    // Sort dates in descending order (newest first)
    const sortedDates = [...allDates].sort((a, b) => b.date.getTime() - a.date.getTime());
    // Get the X most recent months
    const lastXMonths = sortedDates.slice(0, noMonths);
    // Create a map to store brick summaries
    const brickSummaryMap = new Map<string, { regionId: string, salesTotal: number; salesMomentumShort: number; salesMomentumLong: number }>();
    // Initialize all bricks with default values
    bricks.forEach(brick => {
      brickSummaryMap.set(brick.name, {
        regionId: brick.regionId,
        salesTotal: 0,
        salesMomentumShort: brick.salesMomentumShort,
        salesMomentumLong: brick.salesMomentumLong,
      });
    });
    // Calculate sales for each brick in the last 4 months
    bricks.forEach(brick => {
      let brickTotal = 0;
      brick.sales.forEach(sale => {
        const saleYear = this.parseNumber(sale.year);
        const saleMonth = this.parseNumber(sale.month);
        // Check if this sale is in one of the last 4 months
        const isInLastXMonths = lastXMonths.some(({ year, month }) => year === saleYear && month === saleMonth);
        if (isInLastXMonths) {
          brickTotal += this.parseNumber(sale.sales);
        }
      });
      const currentSummary = brickSummaryMap.get(brick.name);
      if (currentSummary) {
        currentSummary.salesTotal = brickTotal;
        brickSummaryMap.set(brick.name, currentSummary);
      }
    });
    // Convert the map to an array of BrickSalesSummary objects
    const result: BrickSalesSummary[] = Array.from(brickSummaryMap.entries())
    .map(([name, { regionId, salesTotal, salesMomentumShort, salesMomentumLong }]) => ({
      name,
      regionId,
      salesTotal,
      salesMomentumShort,
      salesMomentumLong
    }))
    // Sort by salesTotal in descending order (highest first)
    .sort((a, b) => b.salesMomentumShort - a.salesMomentumShort);
    // // Sort by salesTotal in descending order (highest first)
    // .sort((a, b) => b.salesTotal - a.salesTotal);
    return result;
}


  /**
   * Return top and bottom X elements from array
   * @param  {T[]} source
   * @param  {number} x - number of elements on each side
   * @returns T
   */
  getTopAndBottomX<T>(source: T[], x: number): TopAndBottomX<T> {
    if (x === Number.POSITIVE_INFINITY) x = Math.round(source.length / 2);
    const topX = source.slice(0, x);
    const rest = source.slice(x, source.length);
    const btmX = rest.slice(-x);
    const result = { topX, btmX };
    return result;
  }

  /**
   * Sum all brick sales
   * If selectedRegions are provided, filter bricks by selectedRegions
   * @param  {TRegionBrick[]} bricks
   * @param  {Region[]=[]} selectedRegions
   * @returns number
   */
  sumAllBrickSales(bricks: TRegionBrick[], selectedRegions: Region[] = []): number {
    if (!bricks || bricks.length === 0) return 0;
    if (selectedRegions.length) {
      bricks = this.filterBricksBySelectedRegions(bricks, selectedRegions);
    }
    return bricks.map(brick => brick.sales.reduce((sum, a) => sum + +a.sales, 0)).reduce((sum, a) => sum + +a, 0);
  }

  /**
 * Sums all sales, salesInEuro or patients for a given competitor across all provided bricks.
 * Optionally filters bricks by selected regions before summing.
 * @param {string} competitorId - The ID of the competitor to sum sales for.
 * @param {TRegionBrick[]} bricks - Array of region bricks containing sales data.
 * @param {keyof TBrickSale} field - Field to sum.
 * @param {Region[]} [selectedRegions=[]] - Optional array of regions to filter bricks by.
 * @returns {number} The total sales for the specified competitor across the (filtered) bricks.
 */
  sumAllCompetitorData(competitorId: string, bricks: TRegionBrick[], field: keyof TBrickSale, selectedRegions: Region[] = []): number {
    if (!competitorId) return 0;
    if (!bricks || bricks.length === 0) return 0;
    if (selectedRegions.length) {
      bricks = this.filterBricksBySelectedRegions(bricks, selectedRegions);
    }
    return bricks.map(brick => brick.salesCompetitors?.find(comp => comp.competitorId === competitorId)?.sales.reduce((sum, a) => sum + +a[field], 0)).reduce((sum, a) => (sum || 0) + (a || 0), 0) ?? 0;
  }

  /**
   * Sum all region budgets
   * If selected regions are provided, sum only selected
   * @param  {TAccountRegion[]} regions
   * @param  {Region[]=[]} selectedRegions
   * @returns number
   */
  sumAllPlannedBudgets(regions: TAccountRegion[], year: number, selectedRegions: Region[] = []): number {
    if (!regions || regions.length === 0) return 0;
    if (!year) return 0;
    if (selectedRegions.length) {
      regions = this.filterRegionsBySelectedRegions(regions, selectedRegions);
    }
    const result =  regions.map(region => {
      if (region.budgets?.length) {
        return region.budgets.filter(budget => budget.year === year).reduce((sum, a) => sum + +a.plannedBudget, 0)
      }
      return 0;
    }).reduce((sum, a) => sum + +a, 0);
    return result;
  }


  /**
   * Prepare data for momentum calculation
   * @param  {TRegionBrick[]} bricks
   * @returns number
   */
  getDirectSalesMomentumData(bricks: TRegionBrick[], selectedRegions: Region[] = []): number[] {
    if (!bricks || bricks.length === 0) return [];
    let result: TBrickSale[] = [];
    if (selectedRegions.length) {
      bricks = this.filterBricksBySelectedRegions(bricks, selectedRegions);
    }
    bricks.forEach(brick => {
      brick.sales.forEach(sale => {
        result.push({ year: +sale.year, month: +sale.month, sales: +sale.sales, patients: +sale.patients, salesInEuro: +sale.salesInEuro });
      });
    });
    result = this.sortBrickSaleByYearMonth(result);
    return result.map(res => res.sales);
  }

  /**
   * Sort data for momentum calculation
   * @param  {TBrickSale[]} records
   * @returns TBrickSale
   */
  sortBrickSaleByYearMonth(records: TBrickSale[]): TBrickSale[] {
    return [...records].sort((a, b) => {
      // First compare by year
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      // If years are equal, compare by month
      return a.month - b.month;
    });
  }


  filterBricksBySelectedRegions(bricks: TRegionBrick[], selectedRegions: Region[]): TRegionBrick[] {
    if (!bricks || bricks.length === 0) return [];
    let result: TRegionBrick[] = [];
    const selectedRegionsIDs = selectedRegions.map(region => region.id);
    if (selectedRegionsIDs.length) {
      result = bricks.filter(brick => selectedRegionsIDs.includes(brick.regionId));
      return result;
    }
    return bricks;
  }

  filterRegionsBySelectedRegions(regions: TAccountRegion[], selectedRegions: Region[]): TAccountRegion[] {
    if (!regions || regions.length === 0) return [];
    let result: TAccountRegion[] = [];
    const selectedRegionsIDs = selectedRegions.map(region => region.id);
    if (selectedRegionsIDs.length) {
      result = regions.filter(region => selectedRegionsIDs.includes(region.id));
      return result;
    }
    return regions;
  }

  /**
   * Get regions from Project, filter by regions assigned to User
   * @param  {TUser} user
   * @param  {TProject} project
   * @returns Region[]
   */
  getRegions(user: TUser, project: TProject): Region[] {
    let regions: Region[] = [];
    regions = project.accountRegions.map(region => {
      return ({ id: region.id, name: region.name })
    });
    if (user.level !== 0) {
      regions = regions.filter(region => {
        let result = false;
        user.projectsRegions.forEach(userRegions => {
          userRegions.regions.forEach(userRegion => {
            if (region.id === userRegion.id) { result = true; }
          });
        });
        return result;
      });
    }
    return regions;
  }
}
